import moment from 'moment'
import {
  emptySlot,
  slotToPayload,
} from '../../../events/SingleEvent/Shifts/ShiftSlotAssignments'

// Mirrors MAX_REPEAT_DUTIES / MAX_REPEAT_SHIFTS in
// server/src/helpers/PTS/dutyRepeat.js
export const MAX_REPEAT_DUTIES = 100
export const MAX_REPEAT_SHIFTS = 400

export const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ''))

export const repeatDayOptions = [
  { label: 'M', tooltip: 'Monday', value: 1 },
  { label: 'T', tooltip: 'Tuesday', value: 2 },
  { label: 'W', tooltip: 'Wednesday', value: 3 },
  { label: 'T', tooltip: 'Thursday', value: 4 },
  { label: 'F', tooltip: 'Friday', value: 5 },
  { label: 'S', tooltip: 'Saturday', value: 6 },
  { label: 'S', tooltip: 'Sunday', value: 0 },
]

export const defaultRepeat = (start) => ({
  enabled: false,
  daysOfWeek: [moment(start).day()],
  intervalWeeks: 1,
  until: moment(start).add(4, 'weeks').subtract(1, 'day').startOf('day'),
  sameCrew: true,
  // { [occurrence.key]: { [crewTemplate.id]: slot } } - only used when
  // sameCrew is off
  assignments: {},
})

// Moves a time onto another calendar day, keeping its wall-clock time and how
// many days it sits after `anchor`. Done in local time (not by adding a
// duration) so a 07:00 duty is still 07:00 after the clocks change.
const onDay = (day, value, anchor) => {
  const time = moment(value)
  const dayOffset = time
    .clone()
    .startOf('day')
    .diff(moment(anchor).clone().startOf('day'), 'days')
  return moment(day)
    .clone()
    .add(dayOffset, 'days')
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
}

// Every duty the pattern produces. Stops one past the cap so callers can tell
// the user they've gone over it.
export const buildOccurrences = (form, repeat) => {
  if (!repeat?.enabled) return []
  const start = moment(form?.start)
  const end = moment(form?.end)
  const until = moment(repeat.until)
  const days = new Set(repeat.daysOfWeek ?? [])
  if (
    !start.isValid() ||
    !end.isValid() ||
    !end.isAfter(start) ||
    !until.isValid() ||
    !days.size
  ) {
    return []
  }

  const interval = Math.max(1, Number(repeat.intervalWeeks) || 1)
  const firstWeek = start.clone().startOf('isoWeek')
  const cursor = start.clone().startOf('day')
  const occurrences = []

  while (
    cursor.isSameOrBefore(until, 'day') &&
    occurrences.length <= MAX_REPEAT_DUTIES
  ) {
    const weekIndex = cursor
      .clone()
      .startOf('isoWeek')
      .diff(firstWeek, 'weeks')
    if (days.has(cursor.day()) && weekIndex % interval === 0) {
      occurrences.push({
        key: cursor.format('YYYY-MM-DD'),
        day: cursor.clone(),
        start: onDay(cursor, start, start),
        end: onDay(cursor, end, start),
      })
    }
    cursor.add(1, 'day')
  }

  return occurrences
}

// Convert a crew shift on the form (a staged draft from AddShiftModal or an
// unallocated shift from the server) into the EmployeeSelector option shape
// the slot pickers use.
export const slotFromShift = (shift) => {
  if (shift?.assignedToSubcontractor) {
    return { assignedTo: null, assignedToSubcontractor: true }
  }
  const employee = shift?.assignedTo
  if (employee && typeof employee === 'object') {
    return {
      assignedTo: {
        _id: employee.userAccount?._id,
        firstName: employee.userAccount?.firstName,
        lastName: employee.userAccount?.lastName,
        photograph: employee.userAccount?.photograph,
        clinicalRole: employee.clinicalRole,
        employee,
      },
      assignedToSubcontractor: false,
    }
  }
  if (typeof employee === 'string') {
    return {
      assignedTo: { employee: { _id: employee } },
      assignedToSubcontractor: false,
    }
  }
  const subcontractor = shift?.assignedSubcontractor
  if (subcontractor && typeof subcontractor === 'object') {
    return {
      assignedTo: {
        _id: subcontractor.userAccount?._id,
        firstName: subcontractor.userAccount?.firstName,
        lastName: subcontractor.userAccount?.lastName,
        photograph: subcontractor.userAccount?.photograph,
        clinicalRole: subcontractor.clinicalRole,
        isSubcontractor: true,
        subcontractor,
      },
      assignedToSubcontractor: false,
    }
  }
  return emptySlot()
}

// One entry per crew seat on the template duty
export const buildCrewTemplates = (crewShifts = []) =>
  crewShifts.map((shift, index) => ({
    id: String(shift?._id ?? index),
    index,
    shift,
    existing: isObjectId(shift?._id),
    defaultSlot: slotFromShift(shift),
  }))

// An unallocated shift picked on the form is a real record: it can only go
// on the first duty, and its assignment is whatever it already has.
export const isLockedSeat = (occurrenceIndex, template) =>
  occurrenceIndex === 0 && template.existing

// Who fills `template`'s seat on a given duty. With "same crew" on every duty
// copies the template; otherwise the first duty starts with the template's
// crew and the rest start as open vacancies until assigned.
export const getSeatSlot = (repeat, occurrence, occurrenceIndex, template) => {
  if (repeat?.sameCrew || isLockedSeat(occurrenceIndex, template)) {
    return template.defaultSlot
  }
  return (
    repeat?.assignments?.[occurrence.key]?.[template.id] ??
    (occurrenceIndex === 0 ? template.defaultSlot : emptySlot())
  )
}

const seatPersonKey = (slot) => {
  const payload = slotToPayload(slot)
  return payload.assignedTo
    ? `employee:${payload.assignedTo}`
    : payload.assignedSubcontractor
      ? `subcontractor:${payload.assignedSubcontractor}`
      : null
}

// { [occurrenceIndex]: [templateIndex, ...] } for anyone in two seats on the
// same duty (the server rejects these too)
export const findDuplicateSeats = (repeat, occurrences, templates) => {
  const duplicates = {}
  occurrences.forEach((occurrence, occurrenceIndex) => {
    const seen = new Set()
    templates.forEach((template) => {
      const key = seatPersonKey(
        getSeatSlot(repeat, occurrence, occurrenceIndex, template),
      )
      if (!key) return
      if (seen.has(key)) {
        duplicates[occurrenceIndex] = [
          ...(duplicates[occurrenceIndex] ?? []),
          template.index,
        ]
      }
      seen.add(key)
    })
  })
  return duplicates
}

export const getSeatTimes = (occurrence, template, form) => ({
  start: onDay(occurrence.day, template.shift?.start ?? form.start, form.start),
  end: onDay(occurrence.day, template.shift?.end ?? form.end, form.start),
})

// Body for POST /pts/duties/repeat
export const buildRepeatPayload = ({ dutyFields, form, repeat, occurrences, templates }) => ({
  duty: dutyFields,
  occurrences: occurrences.map((occurrence, occurrenceIndex) => ({
    start: occurrence.start.toISOString(),
    end: occurrence.end.toISOString(),
    crewShifts: templates
      .filter((template) => isLockedSeat(occurrenceIndex, template))
      .map((template) => template.shift._id),
    crewShiftDrafts: templates
      .filter((template) => !isLockedSeat(occurrenceIndex, template))
      .map((template) => {
        const { start, end } = getSeatTimes(occurrence, template, form)
        return {
          slot: template.index,
          start: start.toISOString(),
          end: end.toISOString(),
          clinicalRole: template.shift?.clinicalRole,
          payRate: template.shift?.payRate,
          hourlyRate: template.shift?.hourlyRate,
          bonuses: template.shift?.bonuses ?? [],
          travel: template.shift?.travel ?? null,
          shiftNote: template.shift?.shiftNote,
          ...slotToPayload(
            getSeatSlot(repeat, occurrence, occurrenceIndex, template),
          ),
        }
      }),
  })),
})

export const formatDutyRange = (start, end) => {
  const from = moment(start)
  const to = moment(end)
  return to.isSame(from, 'day')
    ? `${from.format('HH:mm')}–${to.format('HH:mm')}`
    : `${from.format('HH:mm')}–${to.format('HH:mm')} (+${to
        .clone()
        .startOf('day')
        .diff(from.clone().startOf('day'), 'days')}d)`
}
